import React, { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { FiActivity, FiArrowRight, FiCalendar, FiHeart, FiMessageCircle, FiShield } from 'react-icons/fi'

const AuthModal = React.lazy(() => import('./AuthModal'))

const welcomeFeatures = [
  { icon: FiHeart, title: 'Remembers you', detail: 'Keeps the moments, preferences, and context that make your connection personal.' },
  { icon: FiCalendar, title: 'Plans with you', detail: 'Brings routines, wellness, meals, movement, and reminders into one manageable day.' },
  { icon: FiMessageCircle, title: 'Always nearby', detail: 'A familiar companion ready to listen, reflect, and help you move forward.' },
  { icon: FiActivity, title: 'Supports wellbeing', detail: 'Helps make meals, movement, recovery, and daily check-ins easier to manage.' },
  { icon: FiShield, title: 'Private by design', detail: 'You control your personal context, including what Twin is allowed to remember.' },
]

const scatterPositions = [
  { x: -165, y: -72, r: -11 },
  { x: 154, y: -82, r: 10 },
  { x: -184, y: 105, r: -6 },
  { x: 176, y: 98, r: 8 },
  { x: 0, y: 137, r: -2 },
]

const ProtectedRoute = ({ children, fallback = null }) => {
  const { isAuthenticated, isInitializing } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [activeFeature, setActiveFeature] = useState(welcomeFeatures.length - 1)
  const [featureTouched, setFeatureTouched] = useState(false)

  useEffect(() => {
    if (isAuthenticated || isInitializing || featureTouched) return undefined
    const timer = window.setInterval(() => {
      setActiveFeature((current) => (current + 1) % welcomeFeatures.length)
    }, 2600)
    return () => window.clearInterval(timer)
  }, [featureTouched, isAuthenticated, isInitializing])

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Twin Companion...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    if (fallback) {
      return fallback
    }

    return (
      <>
        <main className="twin-welcome">
          <div className="twin-welcome__glow twin-welcome__glow--one" />
          <div className="twin-welcome__glow twin-welcome__glow--two" />
          <section className="twin-welcome__hero">
            <div className="twin-welcome__copy">
              <div className="twin-welcome__brand">
                <span className="twin-welcome__brand-mark">T</span>
                <span>Twin Companion</span>
              </div>
              <p className="twin-welcome__eyebrow">Your life, understood</p>
              <h1>More than an AI.<br /><span>A companion for your life.</span></h1>
              <p className="twin-welcome__lead">
                Twin learns what matters to you and helps manage conversations, wellbeing,
                routines, and the everyday details that are easy to carry alone.
              </p>
              <button type="button" onClick={() => setShowAuthModal(true)} className="twin-welcome__cta">
                Get Started <FiArrowRight />
              </button>
              <p className="twin-welcome__privacy">Start free · Private by design · You control your memories</p>
            </div>

            <div className="twin-welcome__experience">
              <div className="twin-welcome__orbit" aria-hidden="true"><span /></div>
              <p className="twin-welcome__deck-label">Tap a card to discover Twin</p>
              <div className="twin-welcome__deck">
                {welcomeFeatures.map((feature, index) => {
                  const Icon = feature.icon
                  const active = index === activeFeature
                  const position = scatterPositions[index]
                  return (
                    <button
                      type="button"
                      key={feature.title}
                      onClick={() => { setFeatureTouched(true); setActiveFeature(index) }}
                      aria-pressed={active}
                      className={`twin-welcome__card ${active ? 'is-active' : ''}`}
                      style={{
                        '--card-x': `${active ? 0 : position.x}px`,
                        '--card-y': `${active ? -22 : position.y}px`,
                        '--card-r': `${active ? 0 : position.r}deg`,
                        '--card-scale': active ? 1.08 : .72,
                        zIndex: active ? 10 : index + 1,
                      }}
                    >
                      <span className="twin-welcome__card-icon"><Icon /></span>
                      <strong>{feature.title}</strong>
                      <span>{active ? feature.detail : 'Bring forward'}</span>
                    </button>
                  )
                })}
              </div>
              <div className="twin-welcome__steps" aria-label={`Feature ${activeFeature + 1} of ${welcomeFeatures.length}`}>
                {welcomeFeatures.map((feature, index) => (
                  <button key={feature.title} type="button" onClick={() => { setFeatureTouched(true); setActiveFeature(index) }} className={index === activeFeature ? 'is-active' : ''} aria-label={`Show ${feature.title}`} />
                ))}
              </div>
            </div>
          </section>
        </main>

        {showAuthModal && (
          <React.Suspense fallback={null}>
            <AuthModal
              isOpen
              onClose={() => setShowAuthModal(false)}
              initialMode="signin"
            />
          </React.Suspense>
        )}
      </>
    )
  }

  return children
}

export default ProtectedRoute
